import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const Booking = () => {
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]); 
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roomId: '',
    checkInDate: '',
    checkOutDate: '',
    adults: 1,
    children: 0,
    specialRequests: ''
  });

  const extractArray = (res, keys = []) => {
    const d = res?.data;
    const dd = d?.data;

    // direct arrays
    if (Array.isArray(d)) return d;
    if (Array.isArray(dd)) return dd;

    // common shapes
    for (const k of keys) {
      if (Array.isArray(dd?.[k])) return dd[k];
      if (Array.isArray(d?.[k])) return d[k];
    }

    // fallback scan first array found
    if (dd && typeof dd === 'object') {
      for (const v of Object.values(dd)) {
        if (Array.isArray(v)) return v;
      }
    }
    if (d && typeof d === 'object') {
      for (const v of Object.values(d)) {
        if (Array.isArray(v)) return v;
      }
    }

    return [];
  };

  const normalizeRoomOptions = (items = []) => {
    return items
      .filter(Boolean)
      .map((r) => {
        const id = r?._id || r?.id || '';
        const name =
          r?.name ||
          r?.title ||
          r?.roomName ||
          r?.roomCategory?.name ||
          'Room';

        const roomNumber = r?.roomNumber || r?.number || '';
        const price =
          r?.basePrice ??
          r?.price ??
          r?.pricing?.basePrice ??
          r?.roomCategory?.basePrice;

        const statusRaw = String(r?.status || '').toLowerCase();
        const isInactive =
          statusRaw === 'inactive' ||
          statusRaw === 'disabled' ||
          statusRaw === 'draft';

        return {
          _id: id,
          name,
          roomNumber,
          basePrice: price,
          status: r?.status,
          kind: 'room',
          label:
            roomNumber && price != null
              ? `${name} (#${roomNumber}) - $${price}/night`
              : roomNumber
              ? `${name} (#${roomNumber})`
              : price != null
              ? `${name} - $${price}/night`
              : name,
          _isInactive: isInactive
        };
      })
      .filter((x) => x._id)
      .filter((x) => !x._isInactive);
  };

  const normalizeCategoryOptions = (items = []) => {
    return items
      .filter(Boolean)
      .map((c) => {
        const id = c?._id || c?.id || '';
        const name = c?.name || c?.title || c?.categoryName || 'Room Type';
        const price = c?.basePrice ?? c?.price ?? c?.startingPrice;
        return {
          _id: id,
          name,
          basePrice: price,
          kind: 'category',
          label: price != null ? `${name} - from $${price}/night` : name
        };
      })
      .filter((x) => x._id);
  };

  useEffect(() => {
    let mounted = true;

    const loadRooms = async () => {
      try {
        // 1) Try actual rooms first
        const roomsRes = await api.get('/api/rooms?limit=100');
        const rawRooms = extractArray(roomsRes, ['rooms', 'items', 'data']);
        const normalizedRooms = normalizeRoomOptions(rawRooms);

        if (normalizedRooms.length > 0) {
          if (mounted) setRooms(normalizedRooms);
          return;
        }

        // 2) Fallback to room categories (the types shown on public rooms page)
        const categoriesRes = await api.get('/api/room-categories');
        const rawCategories = extractArray(categoriesRes, ['categories', 'roomCategories', 'items', 'data']);
        const normalizedCategories = normalizeCategoryOptions(rawCategories);

        if (mounted) {
          setRooms(normalizedCategories);
          if (normalizedCategories.length === 0) {
            setMessage({ type: 'warning', text: 'No rooms available' });
          }
        }
      } catch (e) {
        console.error('Failed to load rooms/categories:', e);
        if (mounted) setMessage({ type: 'error', text: 'Failed to load rooms' });
      } finally {
        if (mounted) setLoadingRooms(false);
      }
    };

    loadRooms();

    return () => { mounted = false; };
  }, []);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };


  const toLocalStartOfDay = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`); // local start
  d.setHours(0, 0, 0, 0);
  return d;
};

const toEndOfDayISO = (dateStr) => {
  if (!dateStr) return dateStr;
  return `${dateStr}T23:59:59`;
}; 

 const validateBeforeSubmit = () => {
  if (form.checkInDate && form.checkOutDate) {
    const inD = new Date(form.checkInDate);
    const outD = new Date(form.checkOutDate);
    if (inD >= outD) {
      setMessage({ type: 'error', text: 'Check-out date must be after check-in date' });
      return false;
    }
  }

  // ✅ Allow booking starting today (disallow only past days)
  if (form.checkInDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const inDay = new Date(`${form.checkInDate}T00:00:00`);
    inDay.setHours(0, 0, 0, 0);

    if (inDay < today) {
      setMessage({ type: 'error', text: 'Check-in date cannot be in the past' });
      return false;
    }
  }

  const adults = Number(form.adults);
  const children = Number(form.children || 0);

  if (!Number.isFinite(adults) || adults < 1) {
    setMessage({ type: 'error', text: 'Adults must be at least 1' });
    return false;
  }

  if (!Number.isFinite(children) || children < 0) {
    setMessage({ type: 'error', text: 'Children cannot be negative' });
    return false;
  }

  return true;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!validateBeforeSubmit()) return;

    setSubmitting(true);

    try {
      const selectedOption = rooms.find((r) => r._id === form.roomId);

      // Keep original payload structure, but add category fallback fields safely
      const payload = {
        guestInfo: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim()
        },
        roomId: selectedOption?.kind === 'room' ? form.roomId : undefined,
        roomCategoryId: selectedOption?.kind === 'category' ? form.roomId : undefined,
        roomCategory: selectedOption?.name || undefined,
        requestedRoomType: selectedOption?.name || undefined,
       checkInDate: toEndOfDayISO(form.checkInDate),
       checkOutDate: toEndOfDayISO(form.checkOutDate),
        numberOfGuests: {
          adults: Number(form.adults),
          children: Number(form.children || 0)
        },
        specialRequests: form.specialRequests
      };

      // remove undefined keys
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const res = await api.post('/api/bookings/request', payload);

      const booking = res?.data?.data?.booking || null;
      const bookingNumber = booking?.bookingNumber || res?.data?.data?.bookingNumber || null;

      const requestForConfirmation = {
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        phone: form.phone,
        email: form.email,
        checkInDate: form.checkInDate,
        checkOutDate: form.checkOutDate,
        adults: Number(form.adults),
        children: Number(form.children || 0),
        roomCategory: selectedOption?.name || '',
        message: form.specialRequests || '',
        bookingNumber: bookingNumber || '',
        autoOpenWhatsApp: true
      };

      navigate('/booking/confirmation', { state: { request: requestForConfirmation } });
    } catch (error) {
      console.error('Booking submit error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit booking request'
      });
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Book Your Stay
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Submit a booking request (Pay at hotel)
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {loadingRooms ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField label="First Name" value={form.firstName} onChange={handleChange('firstName')} required />
          <TextField label="Last Name" value={form.lastName} onChange={handleChange('lastName')} required />
          <TextField label="Email" type="email" value={form.email} onChange={handleChange('email')} required />
          <TextField label="Phone" value={form.phone} onChange={handleChange('phone')} required />

          <TextField
            select
            label="Select Room"
            value={form.roomId}
            onChange={handleChange('roomId')}
            required
          >
            {rooms.length > 0 ? (
              rooms.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  {r.label || r.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No rooms available
              </MenuItem>
            )}
          </TextField>

          <TextField
            label="Check-in"
            type="date"
            value={form.checkInDate}
            onChange={handleChange('checkInDate')}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Check-out"
            type="date"
            value={form.checkOutDate}
            onChange={handleChange('checkOutDate')}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Adults"
            type="number"
            value={form.adults}
            onChange={handleChange('adults')}
            inputProps={{ min: 1 }}
            required
          />

          <TextField
            label="Children"
            type="number"
            value={form.children}
            onChange={handleChange('children')}
            inputProps={{ min: 0 }}
          />

          <TextField
            label="Special Requests"
            value={form.specialRequests}
            onChange={handleChange('specialRequests')}
            multiline
            rows={3}
          />

          <Button type="submit" variant="contained" size="large" disabled={submitting || rooms.length === 0}>
            {submitting ? 'Submitting...' : 'Submit Booking Request'}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default Booking;